import {
  Slider,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FieldLabel } from './FieldLabel';
import { sortFields, type InputValues } from '../inputFieldValues';
import type { PublicInputField } from '../types';

interface ModelInputFieldsProps {
  fields: PublicInputField[];
  values: InputValues;
  onChange: (key: string, value: unknown) => void;
  disabled?: boolean;
}

const MAX_TOGGLE_OPTIONS = 4;

/** Model-specific inputs rendered purely from the field list (no field names in code). */
export const ModelInputFields: React.FC<ModelInputFieldsProps> = ({
  fields,
  values,
  onChange,
  disabled,
}) => {
  const groups = groupFields(sortFields(fields));

  return (
    <div className="space-y-4">
      {groups.map(({ group, items }) => (
        <div key={group ?? ''} className="space-y-3">
          {group && (
            <Typography variant="subtitle2" className="text-gray-700 dark:text-slate-200">
              {group}
            </Typography>
          )}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {items.map((field) => (
              <FieldControl
                key={field.key}
                field={field}
                value={values[field.key]}
                onChange={(value) => onChange(field.key, value)}
                disabled={disabled}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

function groupFields(fields: PublicInputField[]) {
  const groups: Array<{ group?: string; items: PublicInputField[] }> = [];
  for (const field of fields) {
    const existing = groups.find((entry) => entry.group === field.uiGroup);
    if (existing) existing.items.push(field);
    else groups.push({ group: field.uiGroup, items: [field] });
  }
  return groups;
}

interface FieldControlProps {
  field: PublicInputField;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled?: boolean;
}

const FieldControl: React.FC<FieldControlProps> = ({ field, value, onChange, disabled }) => {
  const { t } = useTranslation();
  const wide = field.type === 'string' && field.control === 'textarea';

  return (
    <div className={wide ? 'sm:col-span-2' : undefined}>
      <FieldLabel label={field.label} description={field.description} required={field.required} />
      {renderControl(field, value, onChange, disabled)}
      {field.pricingDimension && (
        <Typography variant="caption" className="block text-gray-400 dark:text-slate-500">
          {t('services.generateVideo.priceDependsHint')}
        </Typography>
      )}
    </div>
  );
};

function renderControl(
  field: PublicInputField,
  value: unknown,
  onChange: (value: unknown) => void,
  disabled?: boolean
) {
  if (field.type === 'enum') return renderEnum(field, value, onChange, disabled);

  if (field.type === 'boolean') {
    return (
      <Switch
        checked={value === true}
        disabled={disabled}
        onChange={(_event, checked) => onChange(checked)}
      />
    );
  }

  if (field.type === 'integer' || field.type === 'number') {
    return renderNumber(field, value, onChange, disabled);
  }

  return (
    <TextField
      fullWidth
      size="small"
      value={typeof value === 'string' ? value : ''}
      disabled={disabled}
      multiline={field.control === 'textarea'}
      minRows={field.control === 'textarea' ? 3 : undefined}
      inputProps={{ maxLength: field.maxLength }}
      helperText={field.maxLength ? `${String(value ?? '').length}/${field.maxLength}` : undefined}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

function renderEnum(
  field: PublicInputField,
  value: unknown,
  onChange: (value: unknown) => void,
  disabled?: boolean
) {
  const options = field.enum ?? [];
  if (options.length <= MAX_TOGGLE_OPTIONS && field.control !== 'select') {
    return (
      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        disabled={disabled}
        value={value ?? null}
        onChange={(_event, next: string | number | null) => {
          if (next !== null) onChange(next);
        }}
      >
        {options.map((option) => (
          <ToggleButton key={String(option)} value={option} className="normal-case">
            {String(option)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    );
  }

  return (
    <TextField
      select
      fullWidth
      size="small"
      value={value ?? ''}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
      SelectProps={{ native: true }}
    >
      {!field.required && <option value="" />}
      {options.map((option) => (
        <option key={String(option)} value={option}>
          {String(option)}
        </option>
      ))}
    </TextField>
  );
}

function renderNumber(
  field: PublicInputField,
  value: unknown,
  onChange: (value: unknown) => void,
  disabled?: boolean
) {
  const step = field.type === 'integer' ? 1 : 'any';
  const useSlider =
    field.control === 'slider' ||
    (field.control !== 'number' && field.min !== undefined && field.max !== undefined);

  if (useSlider && field.min !== undefined && field.max !== undefined) {
    const current = typeof value === 'number' ? value : field.min;
    return (
      <div className="flex items-center gap-3 px-1">
        <Slider
          size="small"
          min={field.min}
          max={field.max}
          step={field.type === 'integer' ? 1 : undefined}
          value={current}
          disabled={disabled}
          onChange={(_event, next) => onChange(Array.isArray(next) ? next[0] : next)}
        />
        <span className="w-10 text-right text-sm text-gray-600 dark:text-slate-300">{current}</span>
      </div>
    );
  }

  return (
    <TextField
      fullWidth
      size="small"
      type="number"
      value={typeof value === 'number' ? value : ''}
      disabled={disabled}
      inputProps={{ min: field.min, max: field.max, step }}
      onChange={(event) => {
        const raw = event.target.value;
        onChange(raw === '' ? undefined : Number(raw));
      }}
    />
  );
}
